import {
  getSortedHazardFeatures,
  HAZARD_MARKERS,
  HazardType,
  MapboxSource,
  MapMode,
  SelectedHazardViewType,
  useHazardsStore,
} from '@/features/hazards';
import { LngLatBoundsLike, LngLatLike, Map as Mapbox, Marker } from 'mapbox-gl';
import { Ref } from 'vue';

import { promisify } from 'es6-promisify';
import { Feature, GeoJsonProperties, Geometry, Point } from 'geojson';
import { generateChecksum } from '@/common';
import { bbox } from '@turf/turf';

const markerElement = (src: string, badge?: number) => {
  const element = document.createElement('image');
  element.className = 'marker size-8 cursor-pointer';

  const image = document.createElement('img');
  image.setAttribute('alt', badge ? 'cluster marker' : 'hazard marker');
  image.setAttribute('src', src);
  element.appendChild(image);

  if (badge) {
    const badgeDiv = document.createElement('div');
    badgeDiv.className =
      'font-proxima-nova text-2xs font-bold text-white absolute top-0 size-4 rounded-full bg-indigo-450 text-center content-end';
    badgeDiv.innerText = badge.toString();
    element.appendChild(badgeDiv);
  }

  return element;
};

const generateClusterClickHandler = (mapbox: Ref<Mapbox | null>, leaves: Feature<Geometry, GeoJsonProperties>[]) => (event: any) => {
  event.preventDefault();
  event.stopPropagation();

  const bounds = bbox({
    type: 'FeatureCollection',
    features: leaves,
  });

  mapbox.value?.fitBounds(bounds as LngLatBoundsLike, {
    padding: { top: 20, bottom: 90, left: 20, right: 80 },
    speed: 5,
  });
};

const generateFeatureClickHandler =
  (eventId: string, hazardId: string, hazardType: HazardType, hazards: ReturnType<typeof useHazardsStore>) => async (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    hazards.toggleSelectHazard({ hazardId, eventId, hazardType, viewType: SelectedHazardViewType.Popup });
  };

/**
 * Manages the markers on the mapbox.value.
 * Clears all markers if the map mode is not {@link MapMode.Default}.
 * Renders the raw DOM markeers built from above, with the click handler dependant on the type
 *
 * Uses `markers` and `clusterMarkersOnScreen` to optimise the rendering by reusing
 * existing markers.
 *
 * Derived from https://docs.mapbox.com/mapbox-gl-js/example/cluster-html/
 */
export const updateMarkers = async (
  mapbox: Ref<Mapbox | null>,
  clusterMarkers: Map<string, Marker>,
  clusterMarkersOnScreen: Map<string, Marker>
) => {
  const hazards = useHazardsStore();

  /// Clear all markers if the map mode is not Default.
  if (!mapbox.value || hazards.mapMode !== MapMode.Default) {
    clusterMarkersOnScreen.forEach((marker) => marker.remove());
    clusterMarkers.clear();
    return new Map<string, Marker>();
  }
  const newMarkers = new Map<string, Marker>();
  const source = mapbox.value.getSource(MapboxSource.hazardsMarkersSource);
  if (!source || source.type != 'geojson') return clusterMarkersOnScreen;

  const clusterFeatures = mapbox.value.querySourceFeatures(MapboxSource.hazardsMarkersSource);

  // for every cluster on the screen, create an HTML marker for it (if we didn't yet),
  // and add it to the map if it's not there already
  for (const feature of clusterFeatures) {
    const coords = (feature.geometry as Point).coordinates as LngLatLike;
    const props = feature.properties!;

    // Non cluster markers
    if (!props.cluster) {
      const markerId = props.timeAwareFeatureId ?? props.featureId ?? props.id;
      let marker = clusterMarkers.get(markerId);
      if (!marker) {
        const src = HAZARD_MARKERS[props['icon-name'] as keyof typeof HAZARD_MARKERS];
        const element = markerElement(src);

        element.onclick = generateFeatureClickHandler(props.eventId, props.id, props.entityType, hazards);

        marker = new Marker({ element }).setLngLat(coords);
        clusterMarkers.set(markerId, marker);
      }

      newMarkers.set(markerId, marker);
      if (!clusterMarkersOnScreen.has(markerId)) marker.addTo(mapbox.value);
      continue;
    }

    // Cluster markers
    let clusterId = props.cluster_id;
    const clusterLeaves = await promisify(source.getClusterLeaves).call(source, clusterId, Infinity, 0);

    const sortedClusterHazards = getSortedHazardFeatures(clusterLeaves);

    if (sortedClusterHazards.length < 1) continue;

    // Compose the clusterId from it's leaves (IDs) to prevent collisions
    clusterId = await generateChecksum(
      sortedClusterHazards.map((hazard) => hazard.properties?.timeAwareFeatureId ?? hazard.properties?.featureId ?? hazard.id).join(',')
    );

    let marker = clusterMarkers.get(clusterId);

    if (!marker) {
      const src = HAZARD_MARKERS[sortedClusterHazards[0].properties?.['icon-name'] as keyof typeof HAZARD_MARKERS];
      const element = markerElement(src, sortedClusterHazards.length);

      element.onclick = generateClusterClickHandler(mapbox, clusterLeaves);

      marker = new Marker({ element }).setLngLat(coords);
      clusterMarkers.set(clusterId, marker);
    }

    newMarkers.set(clusterId, marker);
    if (!clusterMarkersOnScreen.has(clusterId)) marker.addTo(mapbox.value);
  }

  // Remove previously added markers that are no longer visible.
  clusterMarkersOnScreen.forEach((marker, id) => {
    if (newMarkers.has(id)) return;

    marker.remove();
  });

  return newMarkers;
};
